// Slim-Select based searchable <select> initializer (works with Turbo & plain pages)
import SlimSelect from 'slim-select'

function debounce(fn, wait = 250) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), wait)
  }
}

export function enhance(select) {
  if (!select || select.dataset.enhanced === 'true' || select._slim) return
  try {
    const url = select.dataset.selectEnhancerUrlValue
    const minChars = parseInt(select.dataset.selectEnhancerMinCharsValue || '2', 10)
    const instance = new SlimSelect({
      select,
      settings: {
        search: true,
        searchHighlight: true,
        allowDeselect: !!select.dataset.allowDeselect,
        placeholderText: select.getAttribute('placeholder') || '',
      },
    })
    select._slim = instance
    select.dataset.enhanced = 'true'

    if (url) {
      // Attach remote search
      const wrapper = select.nextElementSibling
      const input = wrapper && wrapper.querySelector ? wrapper.querySelector('.ss-search input') : null

      const applyData = (items) => {
        if (typeof instance.setData === 'function') {
          instance.setData(items.map((i) => ({ text: i.text, value: String(i.value) })))
        } else {
          // Fallback: rebuild <select> options then refresh by re-initializing
          const values = Array.from(select.selectedOptions).map((o) => o.value)
          while (select.firstChild) select.removeChild(select.firstChild)
          for (const it of items) {
            const opt = new Option(it.text, String(it.value))
            select.add(opt)
          }
          if (typeof instance.setSelected === 'function' && values.length) {
            instance.setSelected(values)
          }
        }
      }

      const loader = debounce((q) => {
        if (!q || q.length < minChars) return
        fetch(`${url}?q=${encodeURIComponent(q)}`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
          .then((r) => (r.ok ? r.json() : []))
          .then((items) => applyData(items))
          .catch(() => {})
      }, parseInt(select.dataset.selectEnhancerDebounceValue || '300', 10))

      if (input) {
        input.addEventListener('input', (e) => loader(e.target.value))
      }
    }
  } catch (e) {
    console.error('Slim-Select init failed', e, select)
  }
}

export function scan(root = document) {
  root
    .querySelectorAll('select[data-controller="select-enhancer"], select[data-select-enhancer]')
    .forEach((el) => enhance(el))
}

function boot() {
  document.addEventListener('turbo:load', () => scan())
  document.addEventListener('DOMContentLoaded', () => scan())
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType === 1) scan(node)
      }
    }
  })
  observer.observe(document.documentElement, { childList: true, subtree: true })
}

boot()

// Admin can opt into global enhancement by calling enhanceAll
export function enhanceAll(root = document, selector = 'select:not([data-select-enhancer="off"])') {
  root.querySelectorAll(selector).forEach((el) => enhance(el))
}
